import React, { useState } from 'react';
import { contactInfo, socialLinks } from '../data/mock';
import { Send, Mail, Phone, MapPin, ArrowRight, MessageSquare, ExternalLink, Linkedin, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    designation: '',
    industry: '',
    interest: '',
    volume: '',
    timeline: '',
    currentSolution: '',
    fleetSize: '',
    operatingHours: '',
    powerRequirement: '',
    chargingInfra: '',
    operatingEnvironment: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Prepare API payload
    const apiPayload = {
      name: formData.name,
      email: formData.email,
      company: formData.company,
      phone: formData.phone,
      interest: formData.interest,
      message: `
Designation: ${formData.designation}
Industry: ${formData.industry}
Expected Volume: ${formData.volume}
Timeline: ${formData.timeline}

PROJECT DETAILS:
${formData.message}
      `.trim()
    };

    try {
      // Try API submission first
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload)
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        setSubmitted(true);
        toast.success('Thank you! Our engineering team will contact you within 24 hours.');
        // Reset form
        setFormData({
          name: '', email: '', company: '', phone: '', designation: '',
          industry: '', interest: '', volume: '', timeline: '',
          currentSolution: '', fleetSize: '', operatingHours: '',
          powerRequirement: '', chargingInfra: '', operatingEnvironment: '', message: ''
        });
      } else {
        throw new Error(result.error || 'Submission failed');
      }
    } catch (error) {
      console.log('API unavailable, falling back to mailto:', error);
      // Fallback to mailto if API fails
      openMailto();
    } finally {
      setIsSubmitting(false);
    }
  };

  const openMailto = () => {
    const emailBody = `
CONTACT ENQUIRY FROM GEGADYNE WEBSITE

PERSONAL DETAILS
----------------
Name: ${formData.name}
Designation: ${formData.designation}
Company: ${formData.company}
Industry: ${formData.industry}
Email: ${formData.email}
Phone: ${formData.phone}

PRODUCT INTEREST
----------------
Product: ${formData.interest}
Expected Volume: ${formData.volume}
Timeline: ${formData.timeline}
Current Solution: ${formData.currentSolution || 'Not specified'}

PROJECT SPECIFICATIONS
----------------------
Fleet Size: ${formData.fleetSize || 'Not specified'}
Operating Hours: ${formData.operatingHours || 'Not specified'}
Power Requirement: ${formData.powerRequirement || 'Not specified'}
Charging Infrastructure: ${formData.chargingInfra || 'Not specified'}
Operating Environment: ${formData.operatingEnvironment || 'Not specified'}

PROJECT DETAILS
---------------
${formData.message}
    `.trim();

    const subject = encodeURIComponent(`Enquiry from ${formData.name} - ${formData.company}`);
    const body = encodeURIComponent(emailBody);
    const mailtoLink = `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
    
    window.location.href = mailtoLink;
    toast.success('Opening your email client...');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="relative py-24 overflow-hidden" data-testid="contact-section">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-950 to-gray-900" />
      
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
      <div className="absolute inset-0 bg-grid opacity-10" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Side - Info */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
              <MessageSquare className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">Start a Conversation</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {contactInfo.title}
            </h2>
            <p className="text-gray-400 text-base lg:text-lg mb-4 leading-relaxed">
              {contactInfo.subtitle}
            </p>
            <p className="text-gray-500 text-sm mb-10 leading-relaxed">
              We work with operations teams, not purchasing departments. Share your application details and our engineering team will evaluate fit, provide specifications, and outline a pilot pathway.
            </p>

            {/* Contact Info */}
            <div className="space-y-4 mb-8">
              {[
                { icon: Mail, label: 'Email', value: contactInfo.email, href: `mailto:${contactInfo.email}` },
                { icon: Phone, label: 'Phone', value: contactInfo.phone, href: `tel:${contactInfo.phone}` },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <a key={index} href={item.href} className="block" data-testid={`contact-${item.label.toLowerCase()}`}>
                    <div className="flex items-center gap-4 p-4 bg-white/5 rounded-2xl border border-white/10 hover:border-emerald-500/30 transition-colors">
                      <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm">{item.label}</p>
                        <p className="text-white font-medium">{item.value}</p>
                      </div>
                    </div>
                  </a>
                );
              })}
              
              {/* Address with Map Link */}
              <a 
                href={contactInfo.mapUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="block" 
                data-testid="contact-address"
              >
                <div className="flex items-start gap-4 p-4 bg-white/5 rounded-2xl border border-white/10 hover:border-emerald-500/30 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-500 text-sm">Headquarters</p>
                    <p className="text-white font-medium text-sm leading-relaxed">{contactInfo.fullAddress}</p>
                    <span className="inline-flex items-center gap-1 text-emerald-400 text-sm mt-2 hover:underline">
                      View on Maps <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </a>
            </div>

            {/* Google Maps Embed */}
            <div className="mb-8 rounded-2xl overflow-hidden border border-white/10" data-testid="google-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d500!2d72.8712229653386!3d19.123142872260043!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTnCsDA3JzIzLjMiTiA3MsKwNTInMTYuNCJF!5e0!3m2!1sen!2sin!4v1706400000001!5m2!1sen!2sin"
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Gegadyne Energy Location"
                className="grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>

            {/* Social Media */}
            <div className="mb-8">
              <p className="text-gray-400 text-sm mb-4">Follow us</p>
              <div className="flex gap-3">
                <a
                  href={socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-3 bg-white/5 rounded-xl border border-white/10 hover:border-emerald-500/30 hover:bg-emerald-500/10 transition-all group"
                  data-testid="social-linkedin"
                >
                  <Linkedin className="w-5 h-5 text-gray-400 group-hover:text-emerald-400" />
                  <span className="text-gray-400 group-hover:text-white text-sm">LinkedIn</span>
                </a>
                <a
                  href={socialLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-3 bg-white/5 rounded-xl border border-white/10 hover:border-emerald-500/30 hover:bg-emerald-500/10 transition-all group"
                  data-testid="social-twitter"
                >
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-emerald-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  <span className="text-gray-400 group-hover:text-white text-sm">X</span>
                </a>
              </div>
            </div>

            {/* Quick benefits */}
            <div className="p-6 bg-gradient-to-br from-emerald-500/10 to-transparent rounded-2xl border border-emerald-500/20">
              <p className="text-emerald-400 font-semibold mb-3">What happens next?</p>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Engineering review within 24 hours—not a sales call
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Technical specifications matched to your application
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Pilot program proposal with warranty terms
                </li>
              </ul>
              <p className="text-gray-500 text-xs mt-4">
                Most pilot programs begin within 30 days of initial contact.
              </p>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            <h3 className="text-xl font-bold text-white mb-2">Request Technical Consultation</h3>
            <p className="text-gray-400 text-sm mb-6">For enterprise and industrial enquiries only</p>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              {/* Personal Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-400 text-sm font-medium mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm font-medium mb-2">Designation *</label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    required
                    data-testid="contact-designation-input"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                    placeholder="e.g., VP Operations, CTO"
                  />
                </div>
              </div>

              {/* Company Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-400 text-sm font-medium mb-2">Company Name *</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    data-testid="contact-company-input"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                    placeholder="Your company name"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm font-medium mb-2">Industry *</label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    required
                    data-testid="contact-industry-select"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                  >
                    <option value="" className="bg-gray-900">Select industry</option>
                    <option value="Logistics & Warehousing" className="bg-gray-900">Logistics & Warehousing</option>
                    <option value="Manufacturing" className="bg-gray-900">Manufacturing</option>
                    <option value="Automotive" className="bg-gray-900">Automotive</option>
                    <option value="Railways & Metro" className="bg-gray-900">Railways & Metro</option>
                    <option value="Renewable Energy" className="bg-gray-900">Renewable Energy</option>
                    <option value="Telecom & Data Centers" className="bg-gray-900">Telecom & Data Centers</option>
                    <option value="Mining & Heavy Equipment" className="bg-gray-900">Mining & Heavy Equipment</option>
                    <option value="Ports & Shipping" className="bg-gray-900">Ports & Shipping</option>
                    <option value="Aviation (GSE)" className="bg-gray-900">Aviation (GSE)</option>
                    <option value="Defense" className="bg-gray-900">Defense</option>
                    <option value="Other" className="bg-gray-900">Other</option>
                  </select>
                </div>
              </div>

              {/* Contact Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-400 text-sm font-medium mb-2">Work Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm font-medium mb-2">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="contact-phone-input"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
              </div>

              {/* Product Interest */}
              <div>
                <label className="block text-gray-400 text-sm font-medium mb-2">Product Interest *</label>
                <select
                  name="interest"
                  value={formData.interest}
                  onChange={handleChange}
                  required
                  data-testid="contact-interest-select"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                >
                  <option value="" className="bg-gray-900">Select product interest</option>
                  <option value="Traction Battery (Forklifts, AGVs)" className="bg-gray-900">Traction Battery (Forklifts, AGVs)</option>
                  <option value="Energy Storage Systems (ESS)" className="bg-gray-900">Energy Storage Systems (ESS)</option>
                  <option value="Backup Power Solutions" className="bg-gray-900">Backup Power Solutions</option>
                  <option value="Railway & Metro Battery" className="bg-gray-900">Railway & Metro Battery</option>
                  <option value="Battery Validation Services" className="bg-gray-900">Battery Validation Services</option>
                  <option value="Custom Battery Solution" className="bg-gray-900">Custom Battery Solution</option>
                  <option value="Partnership / Distribution" className="bg-gray-900">Partnership / Distribution</option>
                </select>
              </div>

              {/* Project Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-400 text-sm font-medium mb-2">Expected Volume *</label>
                  <select
                    name="volume"
                    value={formData.volume}
                    onChange={handleChange}
                    required
                    data-testid="contact-volume-select"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                  >
                    <option value="" className="bg-gray-900">Select expected volume</option>
                    <option value="Pilot (1-5 units)" className="bg-gray-900">Pilot (1-5 units)</option>
                    <option value="Small (5-20 units)" className="bg-gray-900">Small (5-20 units)</option>
                    <option value="Medium (20-100 units)" className="bg-gray-900">Medium (20-100 units)</option>
                    <option value="Large (100-500 units)" className="bg-gray-900">Large (100-500 units)</option>
                    <option value="Enterprise (500+ units)" className="bg-gray-900">Enterprise (500+ units)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm font-medium mb-2">Project Timeline *</label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    required
                    data-testid="contact-timeline-select"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                  >
                    <option value="" className="bg-gray-900">Select timeline</option>
                    <option value="Immediate (Within 1 month)" className="bg-gray-900">Immediate (Within 1 month)</option>
                    <option value="This Quarter (1-3 months)" className="bg-gray-900">This Quarter (1-3 months)</option>
                    <option value="Within 6 months" className="bg-gray-900">Within 6 months</option>
                    <option value="Within 1 year" className="bg-gray-900">Within 1 year</option>
                    <option value="Exploring / R&D Phase" className="bg-gray-900">Exploring / R&D Phase</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-gray-400 text-sm font-medium mb-2">Project Details *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  data-testid="contact-message-input"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all resize-none"
                  placeholder="Describe your application, power requirements (kWh), operating conditions, and any specific challenges you're facing..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || submitted}
                data-testid="contact-submit-btn"
                className={`w-full font-semibold py-4 rounded-xl flex items-center justify-center gap-2 transition-all group ${
                  submitted 
                    ? 'bg-emerald-600 cursor-default' 
                    : isSubmitting 
                      ? 'bg-emerald-600 cursor-wait' 
                      : 'bg-emerald-500 hover:bg-emerald-400 hover:shadow-lg hover:shadow-emerald-500/25'
                } text-white`}
              >
                {submitted ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Submitted Successfully
                  </>
                ) : isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Enquiry
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>

              <p className="text-gray-500 text-xs text-center">
                {submitted 
                  ? 'Our engineering team will review your enquiry and respond within 24 hours.'
                  : 'Your information is securely submitted directly to our engineering team.'}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
